/**
 * ProjectPermissionBanner — 项目权限拒绝常驻 banner(内联版)
 *
 * R-2026-08-23 重构:
 * - 只显示「当前页面模块」的拒绝(不再累积其它模块的 deny)
 * - 区分查看授权 vs 改写授权:
 *   - 查看级拒绝(no_pmp_record / pmp_no_view / no_org_membership)→ warning「缺少查看权限」
 *   - 编辑级拒绝(pmp_no_edit)→ info「只读,无编辑权限」
 * - 持久化:deny 按「项目:模块」存,切走再切回仍显示(不随路由清空)
 * - 布局:去掉自带的 6px 16px padding,统一交给 .app-page-content 的 14px;底部 12px gap
 */
import { useEffect, useState } from 'react'
import { Alert, Space, Typography } from 'antd'
import { LockOutlined } from '@ant-design/icons'
import { useLocation } from 'react-router-dom'
import { MODULE_NAME_MAP } from '../hooks/usePermissions'

// 查看级拒绝 → 整页无权限(硬拦截);编辑级(pmp_no_edit) → 只读提示
const VIEW_DENY_CODES = new Set(['no_pmp_record', 'pmp_no_view', 'no_org_membership'])

const CODE_TO_REASON = {
  no_pmp_record: '项目层未授权',
  pmp_no_view: '已取消查看',
  pmp_no_edit: '已取消编辑',
  no_org_membership: '不在该项目所属组织',
}

// 模块别名(老 key → canonical),与 usePermissions MODULE_ALIASES 对齐
const MODULE_ALIASES = { quality: 'safety_quality', safety: 'safety_quality' }
const normalizeModule = (m) => MODULE_ALIASES[m] || m

// URL 首段 → module code(镜像 App.jsx 路由表)
function moduleFromPath(pathname) {
  const seg = (pathname.split('/')[1] || '').toLowerCase()
  if (!seg) return null
  const overrides = {
    'safety-quality': 'safety_quality',
    safety: 'safety_quality',
    quality: 'safety_quality',
    'site-contact': 'site_contact',
    'seal-usage': 'seal_usage',
    'document-handover': 'document_handover',
    'work-handover': 'work_handover',
    'work-permit': 'work_permit',
    'shared-data': 'persons',
    'site-management': 'svo',
    'project-health': 'dashboard',
    'platform-admin': 'admin',
    test: 'admin',
    'construction-log': 'meeting',
    // 2026-09-26: photo 是 canonical module key,与 URL 首段同名,显式列出避免依赖 fallback
    photo: 'photo',
    co: null, // 已下架模块,redirect 不走 banner
  }
  if (seg in overrides) return overrides[seg]
  return seg
}

export default function ProjectPermissionBanner() {
  const location = useLocation()
  const [denials, setDenials] = useState({}) // `${projectId}:${module}` -> code

  // 按当前路由的 projectId 存 deny,监听器随 pathname 重建以拿到最新 projectId
  useEffect(() => {
    const pid = location.pathname.split('/')[2] || ''
    const handler = (e) => {
      const { code, module } = e.detail || {}
      if (!module) return
      const key = `${pid}:${normalizeModule(module)}`
      setDenials((prev) => ({ ...prev, [key]: code || 'no_pmp_record' }))
    }
    window.addEventListener('project-permission-denied', handler)
    return () => window.removeEventListener('project-permission-denied', handler)
  }, [location.pathname])

  const pid = location.pathname.split('/')[2] || ''
  const currentModule = moduleFromPath(location.pathname)
  if (!currentModule) return null
  const code = denials[`${pid}:${normalizeModule(currentModule)}`]
  if (!code) return null

  const isViewDenied = VIEW_DENY_CODES.has(code)
  const name = MODULE_NAME_MAP[currentModule] || currentModule
  const reason = CODE_TO_REASON[code] || code
  // 关键字高亮:查看级红、编辑级橙,一眼区分「缺少查看」还是「缺少编辑」
  const keywordColor = isViewDenied ? '#cf1322' : '#d46b08'
  // 背景加深:warning #fffbe6→#fff1b8,info #e6f4ff→#bae0ff
  const bgColor = isViewDenied ? '#fff1b8' : '#bae0ff'

  return (
    <div style={{ marginBottom: 12 }}>
      <Alert
        type={isViewDenied ? 'warning' : 'info'}
        showIcon
        closable
        onClose={() => setDenials((prev) => {
          const next = { ...prev }
          delete next[`${pid}:${normalizeModule(currentModule)}`]
          return next
        })}
        banner
        style={{ background: bgColor, borderRadius: 8, padding: '4px 12px', fontSize: 13, lineHeight: 1.4 }}
        message={
          <Space size={6} wrap>
            <LockOutlined style={{ fontSize: 12 }} />
            {isViewDenied ? (
              <>
                <Typography.Text strong style={{ fontSize: 13 }}>
                  你在当前项目缺少「{name}」模块的
                </Typography.Text>
                <Typography.Text strong style={{ fontSize: 13, color: keywordColor }}>
                  查看权限
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  ({reason})
                </Typography.Text>
              </>
            ) : (
              <>
                <Typography.Text strong style={{ fontSize: 13 }}>
                  「{name}」模块当前为只读,
                </Typography.Text>
                <Typography.Text strong style={{ fontSize: 13, color: keywordColor }}>
                  无编辑权限
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  ({reason})
                </Typography.Text>
              </>
            )}
          </Space>
        }
      />
    </div>
  )
}
